import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../global/supabase';
import { SUPABASE, BRANDING, CLIENT_ID } from '../../config';

const DEPLOYMENT_ID = CLIENT_ID;

const SEVERITY_COLORS = {
  critical: '#d32f2f',
  warning: '#f57c00',
  info: '#1976d2',
};

const STATUS_COLORS = {
  compliant: '#2e7d32',
  needs_attention: '#f57c00',
  non_compliant: '#d32f2f',
};

const PRIORITY_COLORS = {
  high: '#d32f2f',
  medium: '#f57c00',
  low: '#1976d2',
};

const INSIGHT_STATUS_COLORS = {
  generated: '#1976d2',
  reviewed: '#2e7d32',
  dismissed: '#9e9e9e',
};

function AdminInsightsPanel({ userInfo }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    flags: true,
    unitStatus: false,
    trends: false,
    recommendations: false,
  });
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchLatestInsight = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from('admin_insights')
        .select('*')
        .eq('deployment_id', DEPLOYMENT_ID)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }
      setInsight(data || null);
    } catch (err) {
      setError(err.message || 'Failed to fetch insights');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatestInsight();
  }, [fetchLatestInsight]);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      const response = await fetch(
        `${SUPABASE.url}/functions/v1/generate-daily-insights`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ deployment_id: DEPLOYMENT_ID }),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to regenerate: ${errText}`);
      }

      await fetchLatestInsight();
    } catch (err) {
      setError(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!insight) return;
    try {
      const { error: updateError } = await supabase
        .from('admin_insights')
        .update({
          status: newStatus,
          reviewed_by: userInfo?.id || null,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', insight.id);

      if (updateError) throw updateError;
      setInsight((prev) => ({
        ...prev,
        status: newStatus,
        reviewed_by: userInfo?.id,
        reviewed_at: new Date().toISOString(),
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const fourteenDaysAgo = new Date(
        Date.now() - 14 * 24 * 60 * 60 * 1000
      ).toISOString();
      const { data, error: histError } = await supabase
        .from('admin_insights')
        .select('id, summary, flags, status, created_at, model_used, tokens_used')
        .eq('deployment_id', DEPLOYMENT_ID)
        .gte('created_at', fourteenDaysAgo)
        .order('created_at', { ascending: false });

      if (histError) throw histError;
      setHistory(data || []);
    } catch (err) {
      console.error('History fetch error:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Severity counts
  const flags = insight?.flags || [];
  const criticalCount = flags.filter((f) => f.severity === 'critical').length;
  const warningCount = flags.filter((f) => f.severity === 'warning').length;
  const infoCount = flags.filter((f) => f.severity === 'info').length;

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>
        Loading AI Insights...
      </div>
    );
  }

  if (error && !insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#d32f2f' }}>
        <p>{error}</p>
        <button
          onClick={fetchLatestInsight}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!insight) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>
        <p>No AI insights generated yet.</p>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          style={{
            marginTop: '10px',
            padding: '10px 20px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: regenerating ? 'not-allowed' : 'pointer',
            opacity: regenerating ? 0.6 : 1,
          }}
        >
          {regenerating ? 'Generating...' : 'Generate Daily Report'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Severity Badge Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            background: SEVERITY_COLORS.critical,
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {criticalCount} Critical
        </span>
        <span
          style={{
            background: SEVERITY_COLORS.warning,
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {warningCount} Warning
        </span>
        <span
          style={{
            background: SEVERITY_COLORS.info,
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {infoCount} Info
        </span>
        <span
          style={{
            background: INSIGHT_STATUS_COLORS[insight.status] || '#999',
            color: '#fff',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '600',
            marginLeft: 'auto',
          }}
        >
          {insight.status?.toUpperCase()}
        </span>
      </div>

      {/* Executive Summary */}
      <div
        style={{
          background: '#f7fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
        }}
      >
        <h4 style={{ margin: '0 0 8px', color: BRANDING.primaryColor }}>
          Executive Summary
        </h4>
        <p style={{ margin: 0, lineHeight: '1.6', color: '#333' }}>
          {insight.summary || 'No summary available.'}
        </p>
      </div>

      {/* Flags Section */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => toggleSection('flags')}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: expandedSections.flags ? '8px 8px 0 0' : '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
          }}
        >
          <span>Flags ({flags.length})</span>
          <span>{expandedSections.flags ? '▲' : '▼'}</span>
        </button>
        {expandedSections.flags && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              padding: '12px',
            }}
          >
            {flags.length === 0 ? (
              <p style={{ color: '#718096', textAlign: 'center' }}>No flags</p>
            ) : (
              flags.map((flag, i) => (
                <div
                  key={i}
                  style={{
                    borderLeft: `4px solid ${SEVERITY_COLORS[flag.severity] || '#999'}`,
                    padding: '10px 14px',
                    marginBottom: '8px',
                    background: '#fafafa',
                    borderRadius: '0 6px 6px 0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px',
                    }}
                  >
                    <span
                      style={{
                        background: SEVERITY_COLORS[flag.severity] || '#999',
                        color: '#fff',
                        padding: '1px 6px',
                        borderRadius: '3px',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        fontWeight: '600',
                      }}
                    >
                      {flag.severity}
                    </span>
                    <span style={{ fontSize: '12px', color: '#718096' }}>
                      {flag.unit}
                    </span>
                  </div>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#1a202c' }}>
                    {flag.title}
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '2px' }}>
                    {flag.detail}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Unit Status Section */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => toggleSection('unitStatus')}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: expandedSections.unitStatus ? '8px 8px 0 0' : '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
          }}
        >
          <span>Unit Status</span>
          <span>{expandedSections.unitStatus ? '▲' : '▼'}</span>
        </button>
        {expandedSections.unitStatus && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              overflow: 'auto',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ background: '#f7fafc' }}>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Unit</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'center' }}>Logs</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(insight.unit_status || {}).map(
                  ([name, info], i) => (
                    <tr
                      key={i}
                      style={{
                        borderTop: '1px solid #e2e8f0',
                      }}
                    >
                      <td style={{ padding: '10px', fontWeight: '600' }}>
                        {name}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <span
                          style={{
                            background:
                              STATUS_COLORS[info.status] || '#999',
                            color: '#fff',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                          }}
                        >
                          {(info.status || '').replace('_', ' ')}
                        </span>
                      </td>
                      <td
                        style={{ padding: '10px', textAlign: 'center' }}
                      >
                        {info.log_count ?? '-'}
                      </td>
                      <td style={{ padding: '10px', color: '#555' }}>
                        {info.notes || '-'}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Trends Section */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => toggleSection('trends')}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: expandedSections.trends ? '8px 8px 0 0' : '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
          }}
        >
          <span>Trends ({(insight.trends || []).length})</span>
          <span>{expandedSections.trends ? '▲' : '▼'}</span>
        </button>
        {expandedSections.trends && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              padding: '12px',
            }}
          >
            {(insight.trends || []).length === 0 ? (
              <p style={{ color: '#718096', textAlign: 'center' }}>
                No trends identified
              </p>
            ) : (
              (insight.trends || []).map((trend, i) => {
                const arrow =
                  trend.type === 'positive'
                    ? '▲'
                    : trend.type === 'negative'
                    ? '▼'
                    : '—';
                const color =
                  trend.type === 'positive'
                    ? '#2e7d32'
                    : trend.type === 'negative'
                    ? '#d32f2f'
                    : '#555';
                return (
                  <div
                    key={i}
                    style={{
                      padding: '8px 0',
                      borderBottom:
                        i < (insight.trends || []).length - 1
                          ? '1px solid #eee'
                          : 'none',
                    }}
                  >
                    <span style={{ color, fontWeight: 'bold', marginRight: '8px' }}>
                      {arrow}
                    </span>
                    <strong>{trend.title}</strong>: {trend.detail}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Recommendations Section */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => toggleSection('recommendations')}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: expandedSections.recommendations
              ? '8px 8px 0 0'
              : '8px',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
          }}
        >
          <span>
            Recommendations ({(insight.recommendations || []).length})
          </span>
          <span>{expandedSections.recommendations ? '▲' : '▼'}</span>
        </button>
        {expandedSections.recommendations && (
          <div
            style={{
              border: '1px solid #e2e8f0',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              padding: '12px',
            }}
          >
            {(insight.recommendations || []).length === 0 ? (
              <p style={{ color: '#718096', textAlign: 'center' }}>
                No recommendations
              </p>
            ) : (
              (insight.recommendations || []).map((rec, i) => (
                <div
                  key={i}
                  style={{
                    padding: '8px 0',
                    borderBottom:
                      i < (insight.recommendations || []).length - 1
                        ? '1px solid #eee'
                        : 'none',
                  }}
                >
                  <span
                    style={{
                      background: PRIORITY_COLORS[rec.priority] || '#999',
                      color: '#fff',
                      padding: '1px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      fontWeight: '600',
                      marginRight: '8px',
                    }}
                  >
                    {rec.priority}
                  </span>
                  <strong>{rec.title}</strong>: {rec.detail}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
          marginBottom: '16px',
        }}
      >
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          style={{
            padding: '10px 18px',
            background: BRANDING.primaryColor,
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: regenerating ? 'not-allowed' : 'pointer',
            opacity: regenerating ? 0.6 : 1,
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          {regenerating ? 'Generating...' : 'Regenerate Daily'}
        </button>
        {insight.status !== 'reviewed' && (
          <button
            onClick={() => handleStatusUpdate('reviewed')}
            style={{
              padding: '10px 18px',
              background: '#2e7d32',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Mark Reviewed
          </button>
        )}
        {insight.status !== 'dismissed' && (
          <button
            onClick={() => handleStatusUpdate('dismissed')}
            style={{
              padding: '10px 18px',
              background: '#9e9e9e',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            Dismiss
          </button>
        )}
        <button
          onClick={() => {
            setShowHistory(true);
            fetchHistory();
          }}
          style={{
            padding: '10px 18px',
            background: 'transparent',
            color: BRANDING.primaryColor,
            border: `2px solid ${BRANDING.primaryColor}`,
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
          }}
        >
          View History
        </button>
      </div>

      {/* Footer */}
      <div
        style={{
          fontSize: '11px',
          color: '#718096',
          textAlign: 'center',
          padding: '8px 0',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        Model: {insight.model_used || 'N/A'} | Tokens:{' '}
        {insight.tokens_used || 'N/A'} | Generated:{' '}
        {insight.created_at
          ? new Date(insight.created_at).toLocaleString()
          : 'N/A'}
      </div>

      {/* Error Display */}
      {error && (
        <div
          style={{
            marginTop: '10px',
            padding: '10px',
            background: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: '6px',
            color: '#c53030',
            fontSize: '13px',
          }}
        >
          {error}
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowHistory(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3 style={{ margin: 0, color: BRANDING.primaryColor }}>
                Insight History (14 days)
              </h3>
              <button
                onClick={() => setShowHistory(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#718096',
                }}
              >
                ×
              </button>
            </div>

            {historyLoading ? (
              <p style={{ textAlign: 'center', color: '#718096' }}>Loading...</p>
            ) : history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#718096' }}>
                No insights in the last 14 days
              </p>
            ) : (
              history.map((h) => {
                const hFlags = h.flags || [];
                const hCritical = hFlags.filter(
                  (f) => f.severity === 'critical'
                ).length;
                const hWarning = hFlags.filter(
                  (f) => f.severity === 'warning'
                ).length;
                const hInfo = hFlags.filter(
                  (f) => f.severity === 'info'
                ).length;

                return (
                  <div
                    key={h.id}
                    style={{
                      padding: '12px',
                      borderBottom: '1px solid #e2e8f0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                      }}
                    >
                      <span style={{ fontWeight: '600', fontSize: '13px' }}>
                        {new Date(h.created_at).toLocaleString()}
                      </span>
                      <span
                        style={{
                          background:
                            INSIGHT_STATUS_COLORS[h.status] || '#999',
                          color: '#fff',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {h.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                      {hCritical > 0 && (
                        <span
                          style={{
                            color: SEVERITY_COLORS.critical,
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          {hCritical} critical
                        </span>
                      )}
                      {hWarning > 0 && (
                        <span
                          style={{
                            color: SEVERITY_COLORS.warning,
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          {hWarning} warning
                        </span>
                      )}
                      {hInfo > 0 && (
                        <span
                          style={{
                            color: SEVERITY_COLORS.info,
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          {hInfo} info
                        </span>
                      )}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: '12px',
                        color: '#555',
                        lineHeight: '1.4',
                      }}
                    >
                      {h.summary
                        ? h.summary.substring(0, 200) +
                          (h.summary.length > 200 ? '...' : '')
                        : 'No summary'}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminInsightsPanel;
